import { getSavedState, removeState, saveState } from '@state/helpers'
import { getMyOnboardingSteps } from '@src/api/onboarding'

const SHOULD_SHOW_WELCOME_OVERLAY_KEY = 'onboarding.shouldShowWelcomeOverlay'
const HAS_PENDING_ONBOARDING_KEY = 'onboarding.hasPendingOnboarding'
const WELCOME_OVERLAY_PROMPTED_ON_KEY = 'onboarding.welcomeOverlayPromptedOn'
const WELCOME_OVERLAY_VARIANTS = ['first', 'rejected', 'returning']

export function getWelcomeOverlayPromptDateKey(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function normalizeWelcomeOverlayVariant(variant) {
  return WELCOME_OVERLAY_VARIANTS.includes(variant) ? variant : 'first'
}

function normalizePromptedOn(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }

  return value
}

export function hasPromptedWelcomeOverlayToday(promptedOn, variant, date = new Date()) {
  return (
    normalizePromptedOn(promptedOn)[normalizeWelcomeOverlayVariant(variant)] === getWelcomeOverlayPromptDateKey(date)
  )
}

export const state = {
  allSteps: [],
  docusignDocuments: [],
  hasPendingOnboarding: getSavedState(HAS_PENDING_ONBOARDING_KEY, false),
  shouldShowWelcomeOverlay: getSavedState(SHOULD_SHOW_WELCOME_OVERLAY_KEY, false),
  welcomeOverlayPromptedOn: normalizePromptedOn(getSavedState(WELCOME_OVERLAY_PROMPTED_ON_KEY, {})),
}

export const getters = {
  allSteps(state) {
    return state.allSteps
  },
  docusignDocuments(state) {
    return state.docusignDocuments
  },
  hasPendingOnboarding(state, getters, rootState, rootGetters) {
    return rootGetters['user/canAccessOnboarding'] && state.hasPendingOnboarding
  },
  shouldShowWelcomeOverlay(state) {
    return state.shouldShowWelcomeOverlay
  },
  hasShownWelcomeOverlayToday(state) {
    return (variant) => hasPromptedWelcomeOverlayToday(state.welcomeOverlayPromptedOn, variant)
  },
}

export const mutations = {
  SET_ALL_STEPS(state, steps) {
    state.allSteps = steps
  },
  SET_DOCUSIGN_DOCUMENTS(state, documents) {
    state.docusignDocuments = documents || []
  },
  SET_HAS_PENDING_ONBOARDING(state, value) {
    const hasPendingOnboarding = Boolean(value)
    state.hasPendingOnboarding = hasPendingOnboarding
    saveState(HAS_PENDING_ONBOARDING_KEY, hasPendingOnboarding)
  },
  SET_SHOULD_SHOW_WELCOME_OVERLAY(state, value) {
    state.shouldShowWelcomeOverlay = value
    if (value) {
      saveState(SHOULD_SHOW_WELCOME_OVERLAY_KEY, true)
      return
    }

    removeState(SHOULD_SHOW_WELCOME_OVERLAY_KEY)
  },
  MARK_WELCOME_OVERLAY_PROMPTED(state, variant) {
    const promptedOn = {
      ...normalizePromptedOn(state.welcomeOverlayPromptedOn),
      [normalizeWelcomeOverlayVariant(variant)]: getWelcomeOverlayPromptDateKey(),
    }

    state.welcomeOverlayPromptedOn = promptedOn
    saveState(WELCOME_OVERLAY_PROMPTED_ON_KEY, promptedOn)
  },
}

export const actions = {
  async refreshPendingOnboarding({ commit, rootGetters }) {
    if (!rootGetters['user/canAccessOnboarding']) {
      commit('SET_HAS_PENDING_ONBOARDING', false)
      return false
    }

    const { data } = await getMyOnboardingSteps()
    const hasPendingOnboarding = Boolean(data.hasPendingOnboarding ?? data.hasActiveBrokerActivity)
    commit('SET_HAS_PENDING_ONBOARDING', hasPendingOnboarding)

    return hasPendingOnboarding
  },
}
